import { QuickTask } from '@/types/quickTask';
import { StyledPopupShell } from '@/components/common/StyledPopupShell';
import { Button } from '@/components/ui/button';
import { ArrowRight, Target, Trash2, Sun, Clock } from 'lucide-react';

interface MorningReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tasks: QuickTask[];
  onKeep: (id: string) => void;
  onDelete: (id: string) => void;
  onPromoteToGoal: (id: string) => void;
  onComplete: () => void;
  getDaysAgoLabel?: (dateStr: string) => string;
}

export function MorningReviewDialog({
  open,
  onOpenChange,
  tasks,
  onKeep,
  onDelete,
  onPromoteToGoal,
  onComplete,
  getDaysAgoLabel,
}: MorningReviewDialogProps) {
  const handleAction = (taskId: string, action: 'keep' | 'delete' | 'promote') => {
    switch (action) {
      case 'keep':
        onKeep(taskId);
        break;
      case 'delete':
        onDelete(taskId);
        break;
      case 'promote':
        onPromoteToGoal(taskId);
        onDelete(taskId);
        break;
    }
  };

  const remainingTasks = tasks.filter(t => !t.carriedForward);

  if (open && remainingTasks.length === 0 && tasks.length > 0) {
    setTimeout(() => onComplete(), 300);
  }

  const isOpen = open && remainingTasks.length > 0;

  return (
    <StyledPopupShell
      open={isOpen}
      onOpenChange={onOpenChange}
      accent="amber"
      icon={<Sun className="h-6 w-6" />}
      title="Catch-up review"
      description={`You have ${remainingTasks.length} pending task${remainingTasks.length !== 1 ? 's' : ''} from earlier. Take a moment to clear the deck.`}
      footer={
        <Button variant="outline" onClick={onComplete} className="w-full sm:w-auto">
          Skip for now
        </Button>
      }
    >
      <div className="space-y-2 max-h-72 overflow-y-auto -mx-1 px-1">
        {remainingTasks.map(task => (
          <div
            key={task.id}
            className="rounded-xl border bg-card/50 p-3 space-y-2 shadow-sm"
          >
            <div>
              <p className="text-sm font-medium leading-snug break-words">{task.text}</p>
              {getDaysAgoLabel && (
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                  <Clock className="h-3 w-3" />
                  {getDaysAgoLabel(task.createdAt)}
                </p>
              )}
            </div>
            <div className="flex gap-1.5 flex-wrap">
              <Button
                size="sm"
                variant="secondary"
                className="h-8 px-2.5 text-xs"
                onClick={() => handleAction(task.id, 'keep')}
              >
                <ArrowRight className="h-3.5 w-3.5 mr-1" />
                Keep
              </Button>
              <Button
                size="sm"
                className="h-8 px-2.5 text-xs"
                onClick={() => handleAction(task.id, 'promote')}
              >
                <Target className="h-3.5 w-3.5 mr-1" />
                Make it a goal
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 px-2 text-xs text-destructive hover:text-destructive"
                onClick={() => handleAction(task.id, 'delete')}
                aria-label="Delete task"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </StyledPopupShell>
  );
}
